import * as getTypes from '@/store/get-types'
import * as actionTypes from '@/store/action-types'
import * as mutationTypes from '@/store/mutation-types'
import { FulfillmentType } from '@/cowboy/EnumTypes'
import { getAmountWithAppliedFeeIfItExists } from '@/components/shared/mixins/item-fee-mixin'
import { v4 as uuidv4 } from 'uuid'
import { convertCartItemsToProducts } from '@/helpers/analytics/convert-cart-item-to-product'
import { trackItemDetails } from '@/helpers/analytics/track-item-details'
import { segmentModule } from '@/plugins/segment/SegmentModule'
import {
  CART_ITEM_ADDED,
  CART_ITEM_REMOVED,
  CART_ITEM_EDIT
} from '@/plugins/segment/SegmentConstants'

const state = {
  cart: {
    cartItems: []
  },
  showCart: {
    isShown: false,
    fadeOut: false
  }
}

const getters = {
  [getTypes.GET_CART]: state => state.cart,
  [getTypes.GET_CART_ITEMS]: (state, getters) => {
    const catalog = getters[getTypes.GET_CATALOG]
    return catalog ? state.cart.cartItems : []
  },
  [getTypes.GET_CART_EXTERNAL_REF_ID]: state => state.cart.externalRefId,
  [getTypes.GET_PHYSICAL_CART_ITEMS]: state =>
    state.cart.cartItems.filter(
      item => item.fulfillmentType === FulfillmentType.PHYSICAL
    ),
  [getTypes.GET_ELECTRONIC_CART_ITEMS]: state =>
    state.cart.cartItems.filter(
      item => item.fulfillmentType !== FulfillmentType.PHYSICAL
    ),
  [getTypes.GET_PHYSICAL_CART_ITEMS_EXIST]: (state, getters) =>
    !!getters[getTypes.GET_PHYSICAL_CART_ITEMS].length,
  [getTypes.GET_DIGITAL_CART_ITEMS_EXIST]: (state, getters) =>
    !!getters[getTypes.GET_ELECTRONIC_CART_ITEMS].length,
  [getTypes.GET_CART_ITEM]: state => cartItemId => {
    return state.cart.cartItems.find(cartItem => cartItem.id === cartItemId)
  },
  [getTypes.GET_CART_TOTAL]: (state, getters) => {
    return getters[getTypes.GET_CART_ITEMS].reduce((accumulator, cartItem) => {
      let cartItemFee = 0
      if (cartItem.fee && cartItem.fee.cost) {
        cartItemFee = cartItem.fee.cost
      }
      return accumulator + (parseFloat(cartItem.selectedAmount) + cartItemFee)
    }, 0)
  },
  [getTypes.GET_SHOW_CART]: state => state.showCart,
  [getTypes.GET_CART_ITEMS_FORMATTED_AS_PRODUCTS]: (_, getters) => {
    const cartItems = getters[getTypes.GET_CART_ITEMS] || []
    return convertCartItemsToProducts(cartItems)
  }
}

const actions = {
  [actionTypes.ADD_CART_ITEM] ({ commit }, cartItem) {
    commit(mutationTypes.INIT_EXTERNAL_REF_ID, this.cart)
    commit(mutationTypes.ADD_CART_ITEM, cartItem)
  },
  [actionTypes.REMOVE_CART_ITEM] ({ commit, dispatch }, cartItem) {
    commit(mutationTypes.REMOVE_CART_ITEM, cartItem)
    // externalRefId needs to be reset after clearing the cart to
    // eliminate the chance of an idempotent ref collision
    commit(mutationTypes.INIT_EXTERNAL_REF_ID, this.cart)
    dispatch(
      actionTypes.UPDATE_REWARD_LINK_BALANCE,
      getAmountWithAppliedFeeIfItExists(cartItem.selectedAmount, cartItem.fee)
    )
  },
  [actionTypes.UPDATE_ITEM_VALUE] ({ commit, getters }, updateActionPayload) {
    const itemToUpdate = getters[getTypes.GET_CART_ITEM](
      updateActionPayload.cartItemId
    )
    const updateMutationPayload = {
      item: itemToUpdate,
      newAmount: updateActionPayload.newAmount,
      newItemFee: updateActionPayload.newItemFee,
      newUtid: updateActionPayload.newUtid
    }
    commit(mutationTypes.UPDATE_ITEM_VALUE, updateMutationPayload)
  },
  [actionTypes.RESET_CART] ({ commit }) {
    commit(mutationTypes.RESET_CART)
  },
  [actionTypes.SET_CART_EXTERNAL_REF_ID] ({ commit }, newValue) {
    commit(mutationTypes.SET_CART_EXTERNAL_REF_ID, newValue)
  },
  [actionTypes.SET_SHOW_CART] ({ commit }, newShowCartValue) {
    commit(mutationTypes.SET_SHOW_CART, newShowCartValue)
  }
}

const mutations = {
  [mutationTypes.ADD_CART_ITEM] ({ cart }, cartItem) {
    cart.cartItems.push(cartItem)
    segmentModule.track(CART_ITEM_ADDED, trackItemDetails(cartItem))
  },
  [mutationTypes.REMOVE_CART_ITEM] ({ cart }, cartItem) {
    const { cartItems } = cart
    const index = cartItems.indexOf(cartItem)
    if (index !== -1) {
      cartItems.splice(index, 1)
      segmentModule.track(CART_ITEM_REMOVED, trackItemDetails(cartItem))
    }
  },
  [mutationTypes.UPDATE_ITEM_VALUE] (state, updateMutationPayload) {
    const { item } = updateMutationPayload
    item.selectedAmount = updateMutationPayload.newAmount
    item.fee = updateMutationPayload.newItemFee
    if (updateMutationPayload.newUtid) {
      item.utid = updateMutationPayload.newUtid
    }
    state.cart.cartItems = [...state.cart.cartItems]
    segmentModule.track(CART_ITEM_EDIT, trackItemDetails(item))
  },
  [mutationTypes.RESET_CART] (state) {
    state.cart = { cartItems: [], cartTotal: null, externalRefId: uuidv4() }
  },
  [mutationTypes.SET_CART_EXTERNAL_REF_ID] (state, newValue) {
    state.cart.externalRefId = newValue
  },
  [mutationTypes.SET_SHOW_CART] (state, newShowCartValue) {
    state.showCart = newShowCartValue
  },
  [mutationTypes.INIT_EXTERNAL_REF_ID] ({ cart }) {
    // NB: cart externalRefId should be refreshed after any clearing mutative operation
    if (cart.cartItems.length === 0) {
      cart.externalRefId = uuidv4()
    }
  }
}

export default {
  state,
  getters,
  actions,
  mutations
}
